import { projectId, publicAnonKey } from '/utils/supabase/info';

const API_BASE = `https://${projectId}.supabase.co/functions/v1/make-server-ba6cedc4`;

export type BattleRoom = {
  id: string;
  livekitRoomName: string;
  hostUserId: string;
  opponentUserId?: string;
  status: 'waiting' | 'ready' | 'active' | 'ended' | 'cancelled';
  createdAt: string;
  updatedAt: string;
  startsAt?: string;
  endsAt?: string;
  score: {
    host: number;
    opponent: number;
  };
  moderation: {
    mutedUserIds: Array<string>;
    blockedByUserId: Record<string, Array<string>>;
    reports: Array<{ id: string; reporterUserId: string; reason: string; createdAt: string }>;
  };
};

export type BattleEventPayload = {
  type: 'like' | 'gift' | 'mute' | 'block';
  targetUserId?: string;
  idempotencyKey?: string;
  amount?: number;
  pointsDelta?: number;
};

function authHeaders(accessToken?: string | null): Record<string, string> {
  const headers: Record<string, string> = {
    Authorization: `Bearer ${publicAnonKey}`,
    'Content-Type': 'application/json',
  };
  if (accessToken) {
    headers['X-User-Token'] = accessToken;
  }
  return headers;
}

async function parseJson<T>(res: Response): Promise<T> {
  const json = await res.json().catch(() => ({}));
  if (!res.ok) {
    throw new Error((json as any)?.error || 'Battle request failed');
  }
  return json as T;
}

export async function matchmakeBattle(accessToken?: string | null): Promise<{ room: BattleRoom; mode: 'joined' | 'created' }> {
  const res = await fetch(`${API_BASE}/battle/matchmake`, {
    method: 'POST',
    headers: authHeaders(accessToken),
  });
  return parseJson<{ room: BattleRoom; mode: 'joined' | 'created' }>(res);
}

export async function getBattleRoom(roomId: string, accessToken?: string | null): Promise<{ room: BattleRoom }> {
  const res = await fetch(`${API_BASE}/battle/rooms/${roomId}`, {
    method: 'GET',
    headers: authHeaders(accessToken),
  });
  return parseJson<{ room: BattleRoom }>(res);
}

export async function joinBattleRoom(roomId: string, accessToken?: string | null): Promise<{ room: BattleRoom }> {
  const res = await fetch(`${API_BASE}/battle/rooms/${roomId}/join`, {
    method: 'POST',
    headers: authHeaders(accessToken),
  });
  return parseJson<{ room: BattleRoom }>(res);
}

export async function startBattle(roomId: string, accessToken?: string | null): Promise<{ room: BattleRoom }> {
  const res = await fetch(`${API_BASE}/battle/rooms/${roomId}/start`, {
    method: 'POST',
    headers: authHeaders(accessToken),
  });
  return parseJson<{ room: BattleRoom }>(res);
}

export async function endBattle(roomId: string, accessToken?: string | null): Promise<{ room: BattleRoom; winner: string }> {
  const res = await fetch(`${API_BASE}/battle/rooms/${roomId}/end`, {
    method: 'POST',
    headers: authHeaders(accessToken),
  });
  return parseJson<{ room: BattleRoom; winner: string }>(res);
}

export async function postBattleEvent(roomId: string, payload: BattleEventPayload, accessToken?: string | null): Promise<{ room: BattleRoom }> {
  const res = await fetch(`${API_BASE}/battle/rooms/${roomId}/event`, {
    method: 'POST',
    headers: authHeaders(accessToken),
    body: JSON.stringify(payload),
  });
  return parseJson<{ room: BattleRoom }>(res);
}

export async function reportBattleRoom(roomId: string, reason: string, accessToken?: string | null): Promise<{ success: boolean }> {
  const res = await fetch(`${API_BASE}/battle/rooms/${roomId}/report`, {
    method: 'POST',
    headers: authHeaders(accessToken),
    body: JSON.stringify({ reason }),
  });
  return parseJson<{ success: boolean }>(res);
}

export async function mintBattleToken(roomId: string, accessToken?: string | null): Promise<{
  token: string;
  roomName: string;
  livekitUrl: string;
  expiresInSeconds: number;
}> {
  const res = await fetch(`${API_BASE}/battle/rooms/${roomId}/token`, {
    method: 'POST',
    headers: authHeaders(accessToken),
  });
  return parseJson<{
    token: string;
    roomName: string;
    livekitUrl: string;
    expiresInSeconds: number;
  }>(res);
}
